import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { blogService } from '../../services/blogService'

function formatDate(d) {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function BlogsListPage() {
    const [posts, setPosts] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [deleting, setDeleting] = useState(null)

    const load = () => {
        setLoading(true)
        blogService.adminList()
            .then((res) => setPosts(res.data || []))
            .catch((err) => setError(err.message || 'Failed to load'))
            .finally(() => setLoading(false))
    }

    useEffect(() => { load() }, [])

    const handleDelete = async (post) => {
        if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return
        setDeleting(post.id)
        try {
            await blogService.delete(post.id)
            setPosts((prev) => prev.filter((p) => p.id !== post.id))
        } catch (err) {
            alert(err.message || 'Delete failed')
        } finally {
            setDeleting(null)
        }
    }

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-lg font-bold text-admin-heading">Insights</h2>
                    <p className="text-sm text-admin-muted mt-1">Create and manage blog posts published on the public website.</p>
                </div>
                <Link
                    to="/cms/blogs/new"
                    className="flex items-center gap-2 px-4 py-2 text-sm font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all"
                >
                    <PlusIcon className="w-4 h-4" />
                    New Post
                </Link>
            </div>

            {error && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
            )}

            <div className="bg-white border border-admin-border rounded-lg shadow-sm overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" />
                    </div>
                ) : posts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <BlogIcon className="w-12 h-12 text-admin-muted/40 mb-4" />
                        <p className="text-sm font-bold text-admin-heading">No blog posts yet</p>
                        <p className="text-xs text-admin-muted mt-1">Create your first post to get started.</p>
                        <Link
                            to="/cms/blogs/new"
                            className="mt-4 px-4 py-2 text-sm font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all"
                        >
                            New Post
                        </Link>
                    </div>
                ) : (
                    <table className="w-full text-sm table-fixed">
                        <thead>
                            <tr className="border-b border-admin-border bg-slate-50">
                                <th className="px-2 lg:px-5 py-3 text-left text-xs font-bold text-admin-muted uppercase tracking-wider">Title</th>
                                <th className="hidden xl:table-cell px-2 lg:px-5 py-3 text-left text-xs font-bold text-admin-muted uppercase tracking-wider">Slug</th>
                                <th className="px-2 lg:px-5 py-3 text-left text-xs font-bold text-admin-muted uppercase tracking-wider">Status</th>
                                <th className="hidden lg:table-cell px-2 lg:px-5 py-3 text-left text-xs font-bold text-admin-muted uppercase tracking-wider">Published</th>
                                <th className="hidden lg:table-cell px-2 lg:px-5 py-3 text-left text-xs font-bold text-admin-muted uppercase tracking-wider">Created</th>
                                <th className="px-2 lg:px-5 py-3 text-right text-xs font-bold text-admin-muted uppercase tracking-wider w-[110px] lg:w-[160px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-admin-border">
                            {posts.map((post) => (
                                <tr key={post.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-2 lg:px-5 py-3.5 font-medium text-admin-text truncate">
                                        {post.title}
                                    </td>
                                    <td className="hidden xl:table-cell px-2 lg:px-5 py-3.5 text-admin-muted font-mono text-xs truncate">
                                        {post.slug}
                                    </td>
                                    <td className="px-2 lg:px-5 py-3.5">
                                        <StatusBadge status={post.status} />
                                    </td>
                                    <td className="hidden lg:table-cell px-2 lg:px-5 py-3.5 text-admin-muted text-xs whitespace-nowrap">{formatDate(post.published_at)}</td>
                                    <td className="hidden lg:table-cell px-2 lg:px-5 py-3.5 text-admin-muted text-xs whitespace-nowrap">{formatDate(post.created_at)}</td>
                                    <td className="px-2 lg:px-5 py-3.5">
                                        <div className="flex items-center justify-end gap-1 lg:gap-2">
                                            <Link
                                                to={`/cms/blogs/${post.id}`}
                                                className="px-2 lg:px-3 py-1.5 text-xs font-bold text-brand-navy border border-brand-navy/30 rounded hover:bg-brand-navy hover:text-white transition-all"
                                            >
                                                Edit
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(post)}
                                                disabled={deleting === post.id}
                                                className="px-2 lg:px-3 py-1.5 text-xs font-bold text-red-600 border border-red-200 rounded hover:bg-red-50 transition-all disabled:opacity-50"
                                            >
                                                {deleting === post.id ? '…' : 'Delete'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    )
}

function StatusBadge({ status }) {
    return status === 'published' ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-green-50 text-green-700 border border-green-200 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Published
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            Draft
        </span>
    )
}

function PlusIcon({ className }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
}
function SpinnerIcon({ className }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
}
function BlogIcon({ className }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
}
